import React, {RefObject, useEffect, useMemo, useRef, useState} from 'react';
import {
  Box,
  Button,
  Typography,
  Stack,
  IconButton,
  Accordion,
  AccordionDetails,
  Popover,
} from '@mui/material';
import {ReactComponent as DeleteIcon} from '../../../../../assets/images/delete.svg';

import '../PrivGuard.scss';
import {useTranslation} from 'react-i18next';
import ArrButton from '../../../../common/arr-button/ArrButton';
import {ArrowDropDownIcon} from '@mui/x-date-pickers';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrFilterChip from '../../../../common/arr-filter-chip/ArrFilterChip';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../../../assets/images/info-icon.svg';
import {useGetProductionComponents} from '../../../../../hooks/productions/useProductions';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import {TreeViewBaseItem} from '@mui/x-tree-view';
import {ExtendedTreeItemProps} from '../../../../../shared/interfaces';
import TagsTreePanel from '../../common/tags-tree-panel/TagsTreePanel';
import ArrMenu, {
  ArrMenuHandle,
  CustomMenuItem,
} from '../../../../common/arr-menu/ArrMenu';
import {ModuleType} from '../../CreateNewProductionUtils';
import {convertPTagsToLineBreaks} from '../../CreateNewProductionHelpers';

const transformTagTreeData = (
  flatArray: any[]
): TreeViewBaseItem<ExtendedTreeItemProps>[] => {
  const itemMap = new Map();
  const rootItems: TreeViewBaseItem<ExtendedTreeItemProps>[] = [];

  flatArray.forEach((item) => {
    itemMap.set(item.id, {
      id: item.id,
      label: item.text,
      children: [],
      isGroup: item.isGroup,
      type: item.type,
      documentCount: item.documentCount,
      isSelected: item.isSelected,
      ...item,
    });
  });

  flatArray.forEach((item) => {
    const treeItem = itemMap.get(item.id);
    if (item.parent === '#' || item.parent == null) {
      rootItems.push(treeItem);
    } else {
      const parent = itemMap.get(item.parent);
      if (parent) parent.children.push(treeItem);
      else rootItems.push(treeItem);
    }
  });

  return rootItems;
};

interface RuleCardProps {
  title: string;
  onDelete: () => void;
  productionId: number;
  handleChange: (data: SelectionItem[]) => void;
  initialItems?: SelectionItem[];
  isProductionSet: boolean;
  isFirstRender: RefObject<boolean>;
  disabled?: boolean;
  dataTestId: string;
}

type SelectionItem = {
  type: 'tags' | 'reduction' | 'operator';
  label: string;
  objects?: ExtendedTreeItemProps[];
};

const operatorItem = [
  {label: 'AND', id: '1'},
  {label: 'OR', id: '2'},
  {label: 'NOT', id: '3'},
  {label: '(', id: '4'},
  {label: ')', id: '5'},
];

const RuleCard: React.FC<RuleCardProps> = ({
  title,
  onDelete,
  productionId,
  handleChange,
  initialItems,
  isProductionSet,
  isFirstRender,
  disabled = false,
  dataTestId,
}) => {
  const {t} = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const [isTagsFlyoutOpen, setIsTagsFlyoutOpen] = useState(false);
  const [isReductionTagsFlyoutOpen, setIsReductionTagsFlyoutOpen] =
    useState(false);
  const [selectedItems, setSelectedItems] = useState<SelectionItem[]>(
    initialItems || []
  );
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [chipType, setChipType] = useState('');
  const [, setSelectedChip] = useState<SelectionItem | null>(null);
  const [selectedChipIndex, setSelectedChipIndex] = useState<number | null>(
    null
  );
  const [focusChipIndex, setFocusChipIndex] = useState<number | null>(null);

  const [menuDirection, setMenuDirection] = useState<
    'bottom-left' | 'top-left'
  >('bottom-left');
  const [popoverPosition, setPopoverPosition] = useState<'top' | 'bottom'>(
    'bottom'
  );

  const {productionSetId, securityGroupId} = useSelector(
    (state: RootState) => state.production
  );

  // Determine moduleType from isProductionSet using enum
  const moduleType = useMemo(
    () => (isProductionSet ? ModuleType.PRODUCTION : ModuleType.EXPORT),
    [isProductionSet]
  );

  const {data: productionComponentsData} = useGetProductionComponents(
    {
      SecurityGroupId: securityGroupId,
      moduletype: moduleType,
      parentsetid: productionSetId,
      productionId: productionId,
      exportProductionId: 0,
    },
    !!securityGroupId && !!productionSetId && productionId > 0
  );

  const tagTreeData = useMemo(() => {
    return productionComponentsData?.tagTree
      ? transformTagTreeData(productionComponentsData.tagTree)
      : [];
  }, [productionComponentsData]);

  const reductionTagsTreeData = useMemo(() => {
    return productionComponentsData?.redactionTagList
      ? transformTagTreeData(productionComponentsData.redactionTagList)
      : [];
  }, [productionComponentsData]);

  // Sync with parent initialItems
  useEffect(() => {
    if (!isFirstRender.current) {
      if (initialItems?.length) {
        setSelectedItems(initialItems);
        isFirstRender.current = true;
      }
    }
  }, [initialItems]);

  const handleAccordionChange = () => {
    setExpanded(!expanded);
  };

  const handleSelection = (
    data: TreeViewBaseItem<ExtendedTreeItemProps>[],
    sourceType: 'tags' | 'reduction',
    treeData: TreeViewBaseItem<ExtendedTreeItemProps>[]
  ) => {
    if (disabled) return;
    const validSelected = data.filter((item) => item.id !== '-1');

    // If nothing valid remains, stop
    if (validSelected.length === 0) {
      return;
    }
    const selectedIds = validSelected.map((item) => item.id);

    const flattenTree = (
      nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
    ): ExtendedTreeItemProps[] =>
      nodes.reduce<ExtendedTreeItemProps[]>((acc, node) => {
        acc.push(node);
        if (node.children) acc.push(...flattenTree(node.children));
        return acc;
      }, []);

    const flatTree = flattenTree(treeData);
    const selectedObjects = flatTree.filter((node) =>
      selectedIds.includes(node.id)
    );
    const selectedLabels = Array.from(
      new Set(selectedObjects.map((obj) => obj.label))
    ).join(', ');

    const updated = [
      ...selectedItems,
      {type: sourceType, label: selectedLabels, objects: selectedObjects},
    ];
    setSelectedItems(updated);
    handleChange(updated);

    // Close flyout and reset temporary state
    setIsTagsFlyoutOpen(false);
    setIsReductionTagsFlyoutOpen(false);
    setSelectedChip(null);
    setSelectedChipIndex(null);
    setInputValue('');
    setChipType('');
    setOpen(false);
  };

  const menuRef = useRef<ArrMenuHandle | null>(null);
  const chipContainerRef = useRef<HTMLDivElement | null>(null);

  const getSelectionItemA11yLabel = (item: SelectionItem): string => {
    if (item.type === 'tags') {
      return `Tags ${item.label}`;
    }

    if (item.type === 'reduction') {
      return `Redaction ${item.label}`;
    }

    return `Operator ${item.label}`;
  };

  useEffect(() => {
    const chipContainer = chipContainerRef.current;

    if (!chipContainer) return;

    selectedItems.forEach((item, index) => {
      const chipElement = chipContainer.querySelector<HTMLElement>(
        `[data-testid="rule-selected-chip-wrapper-${index}"], [data-testid="rule-card-arr-filter-chip-${index}"], [data-testid="rule-selected-chip-${index}"]`
      );

      if (!chipElement) return;

      const focusableElements = chipElement.querySelectorAll<HTMLElement>(
        'button, [role="button"], [tabindex], input, select, textarea, a[href]'
      );

      if (disabled) {
        chipElement.setAttribute('tabindex', '0');
        chipElement.setAttribute('aria-label', getSelectionItemA11yLabel(item));
        chipElement.setAttribute('aria-disabled', 'true');

        focusableElements.forEach((element) => {
          if (element !== chipElement) {
            element.setAttribute('tabindex', '-1');
          }
          element.setAttribute('aria-disabled', 'true');
        });
        return;
      }

      chipElement.removeAttribute('tabindex');
      chipElement.removeAttribute('aria-label');

      if (item.type === 'operator') {
        focusableElements.forEach((element) => {
          element.setAttribute('tabindex', '-1');
          element.setAttribute('aria-disabled', 'true');
        });
        return;
      }

      const removeControl = Array.from(focusableElements).find(
        (element) =>
          element.tagName.toLowerCase() === 'button' ||
          element.getAttribute('role') === 'button'
      );

      removeControl?.setAttribute('aria-label', `Remove ${item.label}`);
    });
  }, [selectedItems, disabled]);

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (disabled) return;
    setSelectedChipIndex(null);
    setSelectedChip(null);
    const target = e.currentTarget;
    const rect = target.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;

    // If space below is less than 200px → open top
    if (spaceBelow < 250) {
      setMenuDirection('top-left');
    } else {
      setMenuDirection('bottom-left');
    }
    menuRef.current?.open(e.currentTarget as HTMLElement);
  };

  const handleRemove = (id: number) => {
    if (disabled) return;
    const updated = selectedItems.filter((_, i) => i !== id);
    setSelectedItems(updated);
    handleChange(updated);

    // Reset editing state if needed
    setSelectedChip(null);
    setSelectedChipIndex(null);
    setInputValue('');
    setChipType('');
    setOpen(false);
  };

  const onItemClick = (item: CustomMenuItem) => {
    if (disabled) return;
    let updated: SelectionItem[] = [];
    if (selectedChipIndex === null) {
      updated = [
        ...selectedItems,
        {type: 'operator', label: String(item.label)},
      ];
    } else {
      updated = [...selectedItems];
      const clickedChip = updated[selectedChipIndex];
      if (clickedChip.type === 'operator') {
        updated[selectedChipIndex] = {
          ...clickedChip,
          label: String(item.label),
        };
      } else {
        updated.splice(selectedChipIndex + 1, 0, {
          type: 'operator',
          label: String(item.label),
        });
      }
    }
    setSelectedItems(updated);
    handleChange(updated);
    setSelectedChip(null);
    setSelectedChipIndex(null);
    menuRef.current?.close();
    setFocusChipIndex(updated.length - 1);
  };

  useEffect(() => {
    if (focusChipIndex === null) return;

    const timer = setTimeout(() => {
      const el = document.querySelector(
        `[data-testid="rule-selected-chip-${focusChipIndex}-remove-button"]`
      ) as HTMLElement | null;

      if (el) {
        el.focus();
      } else {
        console.log('NOT FOUND');
      }
    }, 5);

    return () => clearTimeout(timer);
  }, [selectedItems, focusChipIndex]);

  const handleChipClick = (
    eventOrEl: React.MouseEvent<HTMLElement> | HTMLElement | null,
    chip: SelectionItem,
    index: number
  ) => {
    if (disabled) return;
    let target: HTMLElement | null = null;

    if (eventOrEl && (eventOrEl as any).currentTarget) {
      target = (eventOrEl as any).currentTarget;
    } else if (eventOrEl instanceof HTMLElement) {
      target = eventOrEl;
    }

    if (!target) return; // <-- SAFETY (fix warning)

    setAnchorEl(target);
    setSelectedChip(chip);
    setSelectedChipIndex(index);
    setInputValue(chip.label || '');
    setChipType(chip.type || '');

    // ---- SAFE SPACE CHECK ----
    const rect = target.getBoundingClientRect?.(); // ?. prevents warning

    if (rect && typeof window !== 'undefined') {
      const spaceBelow = window.innerHeight - rect.bottom;

      if (spaceBelow < 200) {
        setPopoverPosition('top');
      } else {
        setPopoverPosition('bottom');
      }
    }
    setOpen(true);
    // else if (chip.type === 'operator' && target) menuRef.current?.open(target);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedChip(null);
    setSelectedChipIndex(null);
    setOpen(false);
    setInputValue('');
    setChipType('');
  };

  const handleSave = () => {
    if (disabled) return;
    if (selectedChipIndex !== null) {
      const updated = selectedItems.map((item, idx) => {
        if (idx !== selectedChipIndex) return item;

        // ----- FIX: When editing TAGS or REDUCTION -----
        if (item.type === 'tags' || item.type === 'reduction') {
          return {
            ...item,
            label: inputValue.trim(),
            objects: [], // remove old tag objects so old values do NOT remain
          };
        }

        // ----- Operator edit -----
        return {...item, label: inputValue.trim()};
      });

      setSelectedItems(updated);
      handleChange(updated);
    }

    handleClose();
  };

  return (
    <Box className="rule-card">
      <Box
        className="accordheader"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <Box className="title-section">
          <Typography
            className="typo-text subhead4_size title"
            tabIndex={disabled ? 0 : undefined}
            data-testid={`rule-card-title-${dataTestId}`}
          >
            {title}
          </Typography>
          <ArrTooltipWrapper
            title={title}
            closeTooltipOnEscPress
            trigger={
              <IconButton
                aria-label={`${title} info`}
                className="info-icon-button"
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    event.currentTarget.click();
                  }
                }}
              >
                <InfoIcon
                  data-testid={`info-icon-${dataTestId}`}
                  className="filter-panel-info-icon"
                  aria-hidden="true"
                  focusable="false"
                />
              </IconButton>
            }
          >
            {title}
          </ArrTooltipWrapper>
        </Box>

        <Box display="flex" alignItems="center">
          {!disabled && (
            <ArrButton
              variant="secondary"
              size="small"
              className="delete-btn"
              onClick={onDelete}
              dataTestId={`rule-card-button-${dataTestId}`}
            >
              <DeleteIcon />
            </ArrButton>
          )}

          <IconButton
            onClick={handleAccordionChange}
            aria-expanded={expanded}
            aria-label={`${title} collapse toggle`}
            className="accordion-toggle"
            data-testid={`rule-card-expand-${dataTestId}`}
          >
            <ExpandMoreIcon
              className="accordion-arrow"
              style={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s',
              }}
            />
          </IconButton>
        </Box>
      </Box>
      {expanded && (
        <Accordion
          expanded={expanded}
          onChange={handleAccordionChange}
          elevation={0}
          className="accordion"
        >
          <Box className="insetButton">
            <Typography
              className="typo-text label3_size insertText"
              data-testid={`rule-card-priv-guard-insert-text`}
            >
              {t(
                'productionsAndExports.createNewProduction.privGuard.privGuardInsertText'
              )}
              :
            </Typography>

            <ArrButton
              variant="secondary"
              size="small"
              onClick={() => {
                if (disabled) return;
                setSelectedChipIndex(null);
                setSelectedChip(null);
                setIsTagsFlyoutOpen(true);
              }}
              disabled={false}
              isAriaDisabled={disabled}
              aria-disabled={disabled}
              tabIndex={disabled ? 0 : undefined}
              dataTestId="rule-card-priv-guard-insert-btn-tags-button"
            >
              {t(
                'productionsAndExports.createNewProduction.privGuard.privGuardInsertBtnTags'
              )}
            </ArrButton>

            <ArrButton
              variant="secondary"
              size="small"
              onClick={() => {
                if (disabled) return;
                setSelectedChipIndex(null);
                setSelectedChip(null);
                setIsReductionTagsFlyoutOpen(true);
              }}
              disabled={false}
              isAriaDisabled={disabled}
              aria-disabled={disabled}
              tabIndex={disabled ? 0 : undefined}
              dataTestId="rule-card-priv-guard-insert-btn-redaction-button"
            >
              {t(
                'productionsAndExports.createNewProduction.privGuard.privGuardInsertBtnRedaction'
              )}
            </ArrButton>

            <ArrButton
              variant="secondary"
              size="small"
              endIcon={<ArrowDropDownIcon />}
              onClick={handleClick}
              disabled={false}
              isAriaDisabled={disabled}
              aria-disabled={disabled}
              tabIndex={disabled ? 0 : undefined}
              dataTestId="rule-card-priv-guard-insert-btn-operator-button"
            >
              {t(
                'productionsAndExports.createNewProduction.privGuard.privGuardInsertBtnOperator'
              )}
            </ArrButton>

            <ArrMenu
              id="arr-menu-2"
              ref={menuRef}
              items={operatorItem}
              onItemClick={onItemClick}
              direction={menuDirection}
              title=""
            />
          </Box>

          <AccordionDetails>
            <Box className="chipContainer" ref={chipContainerRef}>
              {selectedItems.map((item, index) => (
                <Box
                  key={`${item.type}-${item.label}-${index}`}
                  className="rule-selected-chip-focus-wrapper"
                  data-testid={`rule-selected-chip-wrapper-${index}`}
                  tabIndex={disabled ? 0 : -1}
                  aria-disabled={disabled}
                  aria-label={
                    disabled ? getSelectionItemA11yLabel(item) : undefined
                  }
                >
                  <ArrFilterChip
                    dataTestId={`rule-selected-chip-${index}`}
                    filterName={
                      item.type === 'operator' ? '' : [item.type, '[name']
                    }
                    selections={[
                      {
                        id: index,
                        value: convertPTagsToLineBreaks(
                          item.type === 'operator'
                            ? item.label
                            : `[${item.label}]]`
                        ),
                      },
                    ]}
                    variant={item.type === 'operator' ? 'v1' : 'v2'}
                    classNames={
                      item.type === 'operator' ? 'chip-v1 chip' : 'chip'
                    }
                    isResponsive={false}
                    onRemove={disabled ? undefined : () => handleRemove(index)}
                    clickable={!disabled && item.type !== 'operator'}
                    onClick={
                      disabled
                        ? undefined
                        : (el) => handleChipClick(el as any, item, index)
                    }
                    disabled={disabled}
                    data-testid={`rule-card-arr-filter-chip-${index}`}
                  />
                </Box>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
      {isTagsFlyoutOpen && (
        <TagsTreePanel
          title={t(
            'productionsAndExports.createNewProduction.privGuard.privGuardInsertSelectTags'
          )}
          type="flyout"
          tags={tagTreeData}
          isOpen={isTagsFlyoutOpen}
          onClose={() => setIsTagsFlyoutOpen(false)}
          setSelectedTags={(items) =>
            handleSelection(items, 'tags', tagTreeData)
          }
          selectedButtonName={t(
            'productionsAndExports.createNewProduction.privGuard.privGuardInsertSelectBtn'
          )}
        />
      )}
      {isReductionTagsFlyoutOpen && (
        <TagsTreePanel
          title={t(
            'productionsAndExports.createNewProduction.privGuard.privGuardInsertSelectTags'
          )}
          type="flyout"
          tags={reductionTagsTreeData}
          isOpen={isReductionTagsFlyoutOpen}
          onClose={() => !disabled && setIsReductionTagsFlyoutOpen(false)}
          setSelectedTags={(items) =>
            handleSelection(items, 'reduction', reductionTagsTreeData)
          }
          selectedButtonName={t(
            'productionsAndExports.createNewProduction.privGuard.privGuardInsertSelectBtn'
          )}
        />
      )}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        disablePortal={false}
        marginThreshold={0}
        anchorOrigin={
          popoverPosition === 'top'
            ? {vertical: 'top', horizontal: 'left'}
            : {vertical: 'bottom', horizontal: 'left'}
        }
        transformOrigin={
          popoverPosition === 'top'
            ? {vertical: 'bottom', horizontal: 'left'}
            : {vertical: 'top', horizontal: 'left'}
        }
        PaperProps={{
          className: `
    ${chipType === 'operator' ? 'operatorPopover' : 'chipPopover'} 
    ${popoverPosition === 'top' ? (chipType === 'operator' ? 'operatorPopoverTop' : 'chipPopoverTop') : ''}
  `,
        }}
        // PaperProps={{ className: "chipPopover" }}
      >
        {/* <Stack spacing={1}> */}
        <textarea
          value={inputValue}
          onChange={(e) => {
            if (!disabled) setInputValue(e.target.value);
          }}
          placeholder="Type something..."
          disabled={disabled}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : undefined}
          // variant="outlined"
          // fullWidth
          // multiline
          // minRows={2}
          className="popoverInputText"
          data-testid={`rule-card-textarea`}
        ></textarea>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          className="popoverActions"
        >
          <Button
            size="small"
            onClick={handleClose}
            className="btnCancel"
            disabled={disabled}
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : undefined}
            data-testid="rule-card-cancel-button"
          >
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            className="btnSave"
            onClick={handleSave}
            disabled={disabled}
            aria-disabled={disabled}
            tabIndex={disabled ? -1 : undefined}
            data-testid="rule-card-save-button"
          >
            Save
          </Button>
        </Stack>
        {/* </Stack> */}
      </Popover>
    </Box>
  );
};

export default RuleCard;
